import React, { useEffect, useState } from 'react'
import 'survey-core/modern.min.css'
import Modal from 'react-bootstrap/Modal'
import Button from 'components/Button'
import {
  checkTestFindings,
  getTestFindings,
  submitTestFeedback,
} from 'core/services/api/aiServices'
import { useSelector } from 'react-redux'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import TextareaAutosize from '@mui/material/TextareaAutosize'
import IconButton from '@mui/material/IconButton'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

export interface ISurveyProps {
  showModal: boolean
  onHide: () => void
  selectedFile: any
  isPanelAiEnabled: any
  patientInfo?: any
  selectedPanelIndex?: number
  testName?: string
  onAddToTestFindings: (findings: string) => void
  testDate?: string

}

const AIModal: React.FunctionComponent<ISurveyProps> = (props) => {
  const {
    showModal,
    selectedFile,
    isPanelAiEnabled,
    onAddToTestFindings,
    onHide,
    patientInfo,
    selectedPanelIndex,
    testName,
    testDate
  } = props

  const userDetail = useSelector((state: any) => state?.user?.userDetail)

  const [fileId, setFileId] = useState<string>('')
  const [requestId, setRequestId] = useState<number | null>(null)

  const [AIResults, setAIResults] = useState<any>(null)
  const [modifiedFindings, setModifiedFindings] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [downloadedContent, setDownloadedContent] = useState<string>('')
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false)
  const [downloadError, setDownloadError] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [warning, setWarning] = useState<string>('')
  const [feedbackVerdict, setFeedbackVerdict] = useState<
    'THUMBS_UP' | 'THUMBS_DOWN' | null
  >(null)
  const [feedbackComment, setFeedbackComment] = useState<string>('')
  const [feedbackSubmitting, setFeedbackSubmitting] = useState<boolean>(false)
  const [feedbackSuccess, setFeedbackSuccess] = useState<boolean>(false)
  const [feedbackError, setFeedbackError] = useState<string>('')
  const [existingFeedbackId, setExistingFeedbackId] = useState<number | null>(
    null,
  )
  const [hasSubmittedFeedback, setHasSubmittedFeedback] =
    useState<boolean>(false)

  const applyCompletedResults = (results: any) => {
    const safeResults = results || {}
    setAIResults(safeResults)
    setModifiedFindings(safeResults.cleaned_text || '')
    const warningList =
      safeResults.verification?.warnings || safeResults.warnings || []
    if (Array.isArray(warningList) && warningList.length > 0) {
      setWarning(warningList.join(', '))
    } else if (safeResults.has_warnings) {
      setWarning('AI processing completed with warnings')
    } else {
      setWarning('')
    }
    setLoading(false)
  }

  // Helper function to download HTTP content and convert to base64
  const downloadHttpContent = async (
    url: string,
    fileType: 'image' | 'pdf',
  ): Promise<string> => {
    try {
      setDownloadLoading(true)
      setDownloadError('')

      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(
          `Failed to fetch: ${response.status} ${response.statusText}`,
        )
      }

      const blob = await response.blob()
      let renderedBlob = blob
      if (fileType === 'pdf') {
        renderedBlob = new Blob([blob], { type: 'application/pdf' })
      }

      const blobUrl = URL.createObjectURL(renderedBlob)

      // For PDFs, we'll add the parameters when using it in iframe
      if (fileType === 'pdf') {
        return `${blobUrl}#toolbar=0&navpanes=0&scrollbar=0`
      }

      return blobUrl
    } catch (error) {
      console.error('Error downloading HTTP content:', error)
      setDownloadError(
        error instanceof Error ? error.message : 'Download failed',
      )
      throw error
    } finally {
      setDownloadLoading(false)
    }
  }

  // Helper function to determine file type
  const getFileType = (
    content: string,
    fileName?: string,
  ): 'image' | 'pdf' | 'unknown' => {
    // Check by data URI prefix
    if (content.startsWith('data:image/')) {
      return 'image'
    }
    if (content.startsWith('data:application/pdf')) {
      return 'pdf'
    }

    // Check by file extension for S3 links
    if (fileName) {
      const extension = fileName.split('.').pop()?.toLowerCase()
      if (
        ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension || '')
      ) {
        return 'image'
      }
      if (extension === 'pdf') {
        return 'pdf'
      }
    }

    // Check by URL extension for S3 links
    if (content.startsWith('http')) {
      const urlExtension = content.split('.').pop()?.toLowerCase().split('?')[0] // Remove query params
      if (
        ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(
          urlExtension || '',
        )
      ) {
        return 'image'
      }
      if (urlExtension === 'pdf') {
        return 'pdf'
      }
    }

    return 'unknown'
  }

  // Helper function to download files for user download
  const downloadFile = async (url: string, fileName: string) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error('Error downloading file:', error)
      window.open(url, '_blank')
    }
  }

  // Helper function to download base64 content
  const downloadBase64File = (
    base64Content: string,
    fileName: string,
    mimeType: string,
  ) => {
    try {
      const base64Data = base64Content.replace(/^data:[^;]+;base64,/, '')
      const byteCharacters = atob(base64Data)
      const byteNumbers = new Array(byteCharacters.length)

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }

      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: mimeType })

      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error('Error downloading base64 file:', error)
    }
  }

  // Render file content based on type
  const renderFileContent = () => {
    if (!selectedFile?.content) {
      return <div>No file content available</div>
    }

    if (downloadLoading) {
      return (
        <div className='text-center py-4'>
          <div className='spinner-border' role='status'>
            <span className='sr-only'>Downloading file...</span>
          </div>
          <div className='mt-2'>Downloading file for preview...</div>
        </div>
      )
    }

    if (downloadError) {
      return (
        <div className='text-center py-4 text-danger'>
          <div className='mb-2'>
            <i
              className='fas fa-exclamation-triangle'
              style={{ fontSize: '48px' }}
            ></i>
          </div>
          <div className='mb-2'>Failed to download file for preview</div>
          <div className='small'>{downloadError}</div>
          {selectedFile.content.startsWith('http') && (
            <div className='mt-2'>
              <a
                href={selectedFile.content}
                target='_blank'
                rel='noopener noreferrer'
                className='btn btn-outline-primary btn-sm'
              >
                Open original file
              </a>
            </div>
          )}
        </div>
      )
    }

    const fileType = getFileType(selectedFile.content, selectedFile.name)
    // Use downloaded content if available, otherwise use original
    const displaySource = downloadedContent || selectedFile.content

    switch (fileType) {
      case 'image':
        return (
          <div className='text-center'>
            <img
              src={displaySource}
              alt={selectedFile.name || 'Uploaded image'}
              className='w-100 border border-secondary rounded'
              style={{
                maxHeight: '500px',
                objectFit: 'contain',
              }}
              onError={(e) => {
                console.error('Failed to load image:', e)
                e.currentTarget.src =
                  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkZhaWxlZCB0byBsb2FkPC90ZXh0Pjwvc3ZnPg=='
              }}
            />
            <div className='mt-2'>
              <button
                className='btn btn-outline-primary btn-sm'
                onClick={() => {
                  if (displaySource.startsWith('data:')) {
                    downloadBase64File(
                      displaySource,
                      selectedFile.name || 'image.png',
                      'image/png',
                    )
                  } else {
                    downloadFile(
                      displaySource,
                      selectedFile.name || 'image.png',
                    )
                  }
                }}
              >
                Download Image
              </button>
            </div>
          </div>
        )

      case 'pdf':
        return (
          <div className='text-center'>
            {displaySource.startsWith('data:') ? (
              // For base64 PDF content
              <div>
                <iframe
                  src={displaySource}
                  width='100%'
                  height='500px'
                  className='border border-secondary rounded'
                  title={selectedFile.name || 'PDF Document'}
                />
                <div className='mt-2'>
                  <button
                    className='btn btn-outline-primary btn-sm me-2'
                    onClick={() => window.open(displaySource, '_blank')}
                  >
                    Open PDF in new tab
                  </button>
                  <button
                    className='btn btn-primary btn-sm'
                    onClick={() =>
                      downloadBase64File(
                        displaySource,
                        selectedFile.name || 'document.pdf',
                        'application/pdf',
                      )
                    }
                  >
                    Download PDF
                  </button>
                </div>
              </div>
            ) : (
              // For HTTP URLs (fallback)
              <div>
                <iframe
                  src={displaySource}
                  width='100%'
                  height='500px'
                  className='border border-secondary rounded'
                  title={selectedFile.name || 'PDF Document'}
                />
                <div className='mt-2'>
                  <a
                    href={displaySource}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='btn btn-outline-primary btn-sm me-2'
                  >
                    Open PDF in new tab
                  </a>
                  <button
                    className='btn btn-primary btn-sm'
                    onClick={() =>
                      downloadFile(
                        displaySource,
                        selectedFile.name || 'document.pdf',
                      )
                    }
                  >
                    Download PDF
                  </button>
                </div>
              </div>
            )}
          </div>
        )

      default:
        return (
          <div className='text-center text-muted'>
            <div className='mb-2'>
              <i className='fas fa-file' style={{ fontSize: '48px' }}></i>
            </div>
            <div>Unsupported file type</div>
            <div className='small'>{selectedFile.name}</div>
            {selectedFile.content.startsWith('http') && (
              <div className='mt-2'>
                <button
                  className='btn btn-outline-primary btn-sm'
                  onClick={() =>
                    downloadFile(
                      selectedFile.content,
                      selectedFile.name || 'file',
                    )
                  }
                >
                  Download File
                </button>
              </div>
            )}
          </div>
        )
    }
  }

  const fetchAIResults = async () => {
    setLoading(true)

    try {
      let base64Content = ''
      const fileType = getFileType(selectedFile.content, selectedFile.name)

      let isUrl = false

      if (selectedFile.content.startsWith('data:')) {
        // Already base64 data URI - extract just the base64 part
        base64Content = selectedFile.content.split(',')[1]
      } else if (selectedFile.content.startsWith('http')) {
        // HTTP URL - fetch and convert to base64
        const response = await fetch(selectedFile.content)
        const blob = await response.blob()

        const dataUri = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })

        // Extract base64 string from data URI
        base64Content = dataUri.split(',')[1]

        // Also download for display
        if (fileType !== 'unknown') {
          const displayContent = await downloadHttpContent(
            selectedFile.content,
            fileType,
          )
          setDownloadedContent(displayContent)
        }
        isUrl = true
      } else if (selectedFile.content.startsWith('blob:')) {
        // Blob URL - convert to base64
        const response = await fetch(selectedFile.content)
        const blob = await response.blob()

        const dataUri = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })

        // Extract base64 string from data URI
        base64Content = dataUri.split(',')[1]
      } else {
        // Assume it's already a base64 string without data URI prefix
        base64Content = selectedFile.content
      }

      const requestId =
        `${patientInfo.mocId}_${selectedPanelIndex}_${selectedFile.name}`.replace(
          /\//g,
          '-',
        )
      // create file object with an optional source_url property (typed as any to avoid TS error)
      const file: any = {
        original_filename: selectedFile.name,
        ...(isUrl
          ? { source_url: selectedFile.content }
          : { data_string: base64Content }),
      }

      const body = {
        external_request_id: requestId,
        patient_id: patientInfo.mocId,
        patient_name: patientInfo.name,
        file_name: selectedFile.name,
        test_name: testName,
        test_date: testDate,
        triggered_by_user_id: userDetail?.id,
        files: [file],
      }
      // Send ONLY the base64 string (without data: prefix) to getTestFindings
      const response = await getTestFindings(body)
      const nextRequestId =
        typeof response?.request_id === 'number'
          ? response.request_id
          : typeof response?.id === 'number'
            ? response.id
            : null
      setRequestId(nextRequestId)
      const pollId = response?.external_request_id || body.external_request_id
      const loadingStatuses = [
        'QUEUED',
        'OCR_IN_PROGRESS',
        'VERIFICATION_IN_PROGRESS',
      ]
      const failedStatuses = [
        'FAILED',
        'OCR_FAILED',
        'VERIFICATION_FAILED'
      ]

      if (
        response.status === 'COMPLETED' ||
        response.status === 'COMPLETED_WITH_WARNINGS'
      ) {
        setFileId('')
        setError('')
        applyCompletedResults(response)
        return
      }

      if (failedStatuses.includes(response.status)) {
        setFileId('')
        setError(response?.error?.message || 'AI processing failed')
        setAIResults({})
        setModifiedFindings('')
        setWarning('')
        setLoading(false)
        return
      }

      if (loadingStatuses.includes(response.status)) {
        setFileId(pollId)
      } else {
        setFileId('')
        setLoading(false)
      }
    } catch (error) {
      console.error('Error fetching AI results:', error)
      setError('Error processing file for AI analysis')
      setAIResults({})
      setModifiedFindings('')
      setLoading(false)
    }
  }
  const handleFeedbackSubmit = async (verdict: 'THUMBS_UP' | 'THUMBS_DOWN') => {
    if (!requestId) {
      setFeedbackError('No request ID available for feedback')
      return
    }

    if (verdict === 'THUMBS_DOWN' && !feedbackComment.trim()) {
      setFeedbackVerdict(verdict)
      return
    }

    setFeedbackSubmitting(true)
    setFeedbackError('')

    try {
      const feedbackData: any = {
        verdict,
        feedback_user_id: userDetail?.id,
      }

      if (verdict === 'THUMBS_DOWN' && feedbackComment.trim()) {
        feedbackData.comment = feedbackComment.trim()
      }

      const response = await submitTestFeedback(requestId, feedbackData)
      if (response.id) {
        setExistingFeedbackId(response.id)
      }

      setFeedbackSuccess(true)
      setFeedbackVerdict(verdict)
      setHasSubmittedFeedback(true)
      setFeedbackComment('')
      setTimeout(() => {
        setFeedbackSuccess(false)
      }, 3000)
    } catch (error) {
      console.error('Error submitting feedback:', error)
      setFeedbackError('Failed to submit feedback. Please try again.')
    } finally {
      setFeedbackSubmitting(false)
    }
  }

  // Handle thumbs down with comment submission
  const handleThumbsDownSubmit = () => {
    if (!feedbackComment.trim()) {
      setFeedbackError('Please provide a comment for thumbs down feedback')
      return
    }
    handleFeedbackSubmit('THUMBS_DOWN')
  }
  useEffect(() => {
    if (fileId) {
      const interval = setInterval(async () => {
        try {
          const response = await checkTestFindings(fileId)
          if (response.request_id && !requestId) {
            setRequestId(response.request_id)
          }
          if (
            response.status === 'FAILED' ||
            response.status === 'OCR_FAILED' ||
            response.status === 'VERIFICATION_FAILED'
          ) {
            clearInterval(interval)
            setError(response.error.message || 'AI processing failed')
            setAIResults({})
            setModifiedFindings('')
            setWarning('')
            setLoading(false)
          } else if (response.status === 'COMPLETED_WITH_WARNINGS') {
            clearInterval(interval)
            setError('')
            applyCompletedResults(response)
          } else if (response.status === 'COMPLETED') {
            clearInterval(interval)
            setError('')
            applyCompletedResults(response)
          }
        } catch (error) {
          console.error('Error fetching AI results:', error)
          clearInterval(interval)
          setAIResults({ findings: 'Error processing file for AI analysis' })
          setModifiedFindings('Error processing file for AI analysis')
          setLoading(false)
        }
      }, 5000) // Poll every 5 seconds

      return () => clearInterval(interval)
    }
  }, [fileId])
  useEffect(() => {
    // Reset states when modal opens/closes or file changes
    if (showModal) {
      setAIResults(null)
      setModifiedFindings('')
      setError('')
      setWarning('')
      setFileId('')
      setLoading(true)
      setDownloadedContent('')
      setDownloadError('')
      setFeedbackVerdict(null)
      setFeedbackComment('')
      setFeedbackSuccess(false)
      setFeedbackError('')
      setRequestId(null)
      setExistingFeedbackId(null)
      setHasSubmittedFeedback(false)
      if (selectedFile && isPanelAiEnabled) {
        fetchAIResults()
      } else {
        setLoading(false)
      }
    } else {
      setAIResults(null)
      setModifiedFindings('')
      setError('')
      setWarning('')
      setFileId('')
      setLoading(false)
      setDownloadedContent('')
      setDownloadError('')
      setFeedbackVerdict(null)
      setFeedbackComment('')
      setFeedbackSuccess(false)
      setFeedbackError('')
      setRequestId(null)
      setExistingFeedbackId(null)
      setHasSubmittedFeedback(false)
    }
  }, [showModal, selectedFile, isPanelAiEnabled])

  const hasNgsTablesMarkdown = Boolean(AIResults?.ngs_tables_markdown)

  return (
    <Modal
      size="xl"
      show={showModal}
      onHide={onHide}
    >
      <Modal.Header closeButton>
        Add to Test Findings
      </Modal.Header>
      <Modal.Body
        style={
          hasNgsTablesMarkdown
            ? { maxHeight: '80vh', overflowY: 'auto' }
            : undefined
        }
      >
        {loading ? (
          <div className='text-center py-4'>
            <div className='spinner-border' role='status'>
              <span className='sr-only'>Something is brewing...</span>
            </div>
            <div className='mt-2'>Processing AI analysis...</div>
          </div>
        ) : selectedFile ? (
          isPanelAiEnabled ? (
            <div className='d-flex flex-column flex-lg-row gap-3 w-100'>
              <div className='w-100'>
                {error ? (
                  <div className='text-center py-4 text-danger'>
                    <div className='mb-2'>
                      <i
                        className='fas fa-exclamation-triangle'
                        style={{ fontSize: '48px' }}
                      ></i>
                    </div>
                    <div className='mb-2'>{error}</div>
                  </div>
                ) : (
                  <>
                    <div className='mb-3 d-flex justify-content-between align-items-center'>
                      {typeof AIResults?.ocr?.accuracy_score === 'number'
                        ? `${(AIResults.ocr.accuracy_score * 100).toFixed(2)}%`
                        : '--'}
                      <Button
                        text="Add to test findings"
                        onClick={() => { onAddToTestFindings(modifiedFindings) }}
                      />
                    </div>
                    {hasNgsTablesMarkdown && (
                      <div className='mb-3'>
                        <div className='fw-bold mb-2'>NGS Tables</div>
                        <div
                          className='border rounded bg-white p-3'
                          style={{ maxHeight: '40vh', overflowY: 'auto' }}
                        >
                          <div style={{ overflowX: 'auto', width: '100%' }}>
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                table: ({ children }) => (
                                  <table
                                    style={{
                                      width: '100%',
                                      minWidth: '720px',
                                      borderCollapse: 'collapse',
                                      borderSpacing: 0,
                                      marginBottom: '16px',
                                    }}
                                  >
                                    {children}
                                  </table>
                                ),
                                thead: ({ children }) => (
                                  <thead style={{ background: '#f8f9fa' }}>
                                    {children}
                                  </thead>
                                ),
                                th: ({ children }) => (
                                  <th
                                    style={{
                                      textAlign: 'left',
                                      verticalAlign: 'top',
                                      padding: '10px 12px',
                                      border: '1px solid #e3e6ea',
                                      fontWeight: 600,
                                      whiteSpace: 'normal',
                                    }}
                                  >
                                    {children}
                                  </th>
                                ),
                                td: ({ children }) => (
                                  <td
                                    style={{
                                      verticalAlign: 'top',
                                      padding: '10px 12px',
                                      border: '1px solid #e3e6ea',
                                    }}
                                  >
                                    {children}
                                  </td>
                                ),
                              }}
                            >
                              {AIResults.ngs_tables_markdown}
                            </ReactMarkdown>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className='mb-3'>
                      <textarea
                        className='form-control'
                        rows={10}
                        value={modifiedFindings}
                        onChange={(e) => setModifiedFindings(e.target.value)}
                      />
                    </div>
                    {AIResults?.verification?.warnings &&
                      AIResults.verification.warnings.length > 0 && (
                        <div className='mt-3 p-3 rounded bg-warning bg-opacity-10 border border-warning'>
                          <div
                            className='fw-bold mb-2'
                            style={{ fontSize: '14px' }}
                          >
                            <i className='fas fa-exclamation-triangle text-warning'></i>
                            Validation Warnings
                          </div>
                          {AIResults.verification.warnings.map(
                            (warning: string, index: number) => (
                              <div
                                key={index}
                                className='mb-1'
                                style={{ fontSize: '13px' }}
                              >
                                {warning}
                              </div>
                            ),
                          )}
                        </div>
                      )}
                    {/* If ever notes required in future */}
                    {/* {AIResults?.verification?.validation_details?.notes && (
                  <div className="mt-3 p-2 rounded bg-light text-muted" style={{ fontSize: '12px' }}>
                    <i className="fas fa-info-circle me-1"></i>
                    {AIResults.verification.validation_details.notes}
                  </div>
                )} */}
                    <div className='mt-2 mb-3 p-3 border rounded bg-light'>
                      <div className='d-flex align-items-center gap-2 mb-2'>
                        <span className='fw-bold'>
                          {hasSubmittedFeedback
                            ? 'Your rating:'
                            : 'Rate this result:'}
                        </span>

                        <IconButton
                          onClick={() => handleFeedbackSubmit('THUMBS_UP')}
                          disabled={feedbackSubmitting}
                          color={
                            feedbackVerdict === 'THUMBS_UP'
                              ? 'success'
                              : 'default'
                          }
                          size='small'
                          title={
                            hasSubmittedFeedback &&
                              feedbackVerdict === 'THUMBS_UP'
                              ? 'Current rating'
                              : 'Good result'
                          }
                        >
                          <ThumbUpIcon />
                        </IconButton>

                        <IconButton
                          onClick={() => handleFeedbackSubmit('THUMBS_DOWN')}
                          disabled={feedbackSubmitting}
                          color={
                            feedbackVerdict === 'THUMBS_DOWN'
                              ? 'error'
                              : 'default'
                          }
                          size='small'
                          title={
                            hasSubmittedFeedback &&
                              feedbackVerdict === 'THUMBS_DOWN'
                              ? 'Current rating'
                              : 'Needs improvement'
                          }
                        >
                          <ThumbDownIcon />
                        </IconButton>

                        {feedbackSubmitting && (
                          <span
                            className='spinner-border spinner-border-sm ms-2'
                            role='status'
                          >
                            <span className='sr-only'>Submitting...</span>
                          </span>
                        )}

                        {hasSubmittedFeedback && !feedbackSubmitting && (
                          <span className='text-muted small ms-2'>
                            {/* here we prompt the user to use other icon to click */}
                          </span>
                        )}
                      </div>

                      {feedbackSuccess && (
                        <div
                          className='alert alert-success py-2 mb-2'
                          role='alert'
                        >
                          <small>
                            {hasSubmittedFeedback && existingFeedbackId
                              ? 'Feedback updated successfully!'
                              : 'Thank you for your feedback!'}
                          </small>
                        </div>
                      )}

                      {feedbackError && (
                        <div
                          className='alert alert-danger py-2 mb-2'
                          role='alert'
                        >
                          <small>{feedbackError}</small>
                        </div>
                      )}

                      {feedbackVerdict === 'THUMBS_DOWN' && (
                        <div className='mt-2'>
                          <TextareaAutosize
                            maxRows={4}
                            minRows={2}
                            aria-label='feedback comment'
                            placeholder='Please tell us your feedbacks'
                            value={feedbackComment}
                            onChange={(e) => setFeedbackComment(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              borderRadius: '4px',
                              border: '1px solid #ced4da',
                              fontFamily: 'inherit',
                            }}
                            disabled={feedbackSubmitting}
                          />
                          {!hasSubmittedFeedback && (
                            <div className='mt-2'>
                              <Button
                                text='Submit Feedback'
                                onClick={handleThumbsDownSubmit}
                                disabled={
                                  feedbackSubmitting || !feedbackComment.trim()
                                }
                              />
                            </div>
                          )}
                          {hasSubmittedFeedback && feedbackComment && (
                            <div className='mt-2'>
                              <Button
                                text='Update Comment'
                                onClick={handleThumbsDownSubmit}
                                disabled={
                                  feedbackSubmitting ||
                                  feedbackComment.trim().length < 20
                                }
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className='w-100'>{renderFileContent()}</div>
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="mb-3">AI Analysis Not Available</div>
              <div className="text-muted">AI processing is not currently supported for this panel</div>
              <div className="text-muted">AI processing is only available for CT, PET-CT, Histopathology, MRI and NGS</div>
            </div>
          )) : (
          <div className="text-center py-4">
            <div className="mb-3">Please upload a file to begin AI analysis</div>
            <div className="text-muted">Supported formats: Images (PNG, JPG) and PDF documents</div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default AIModal
